import { memo, Suspense } from 'react'
import { PieChart, Pie, Cell, ResponsiveContainer, Sector } from 'recharts'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { PieChart as PieChartIcon } from 'lucide-react'
import type { StatusDistribution } from '@/lib/jira/metrics'

interface JiraStatusDonutChartProps {
  data: StatusDistribution[]
  title?: string
  height?: number
  loading?: boolean
  className?: string
  showLegend?: boolean
}

// Aktif segment render fonksiyonu
const renderActiveShape = (props: any) => {
  const {
    cx,
    cy,
    innerRadius,
    outerRadius,
    startAngle,
    endAngle,
    fill,
    payload,
    percent,
    value,
  } = props

  return (
    <g>
      <text x={cx} y={cy - 10} textAnchor="middle" className="fill-gray-900 dark:fill-white text-sm font-semibold">
        {payload.name}
      </text>
      <text x={cx} y={cy + 10} textAnchor="middle" className="fill-gray-600 dark:fill-gray-400 text-xs">
        {value} ({((percent ?? 0) * 100).toFixed(0)}%)
      </text>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius + 8}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
      />
      <Sector
        cx={cx}
        cy={cy}
        startAngle={startAngle}
        endAngle={endAngle}
        innerRadius={outerRadius + 12}
        outerRadius={outerRadius + 14}
        fill={fill}
      />
    </g>
  )
}

const ChartContent = memo(function ChartContent({
  data,
  height = 240,
}: Pick<JiraStatusDonutChartProps, 'data' | 'height'>) {
  const onPieEnter = () => {
    // Recharts handles active state internally
  }
  
  const onPieLeave = () => {
    // Recharts handles active state internally
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <PieChart>
        <Pie
          activeShape={renderActiveShape}
          data={data as any}
          cx="50%"
          cy="50%"
          innerRadius={60}
          outerRadius={80}
          dataKey="value"
          onMouseEnter={onPieEnter}
          onMouseLeave={onPieLeave}
        >
          {data.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={entry.color} />
          ))}
        </Pie>
      </PieChart>
    </ResponsiveContainer>
  )
})

const Legend = memo(function Legend({ data }: { data: StatusDistribution[] }) {
  return (
    <div className="grid grid-cols-2 gap-2 mt-4">
      {data.slice(0, 6).map((item) => (
        <div key={item.name} className="flex items-center gap-2">
          <div
            className="w-3 h-3 rounded-full flex-shrink-0"
            style={{ backgroundColor: item.color }}
          />
          <div className="min-w-0 flex-1">
            <p className="text-xs font-medium text-gray-700 dark:text-gray-300 truncate">
              {item.name}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {item.value} ({item.percentage}%)
            </p>
          </div>
        </div>
      ))}
    </div>
  )
})

const LoadingState = memo(function LoadingState({ height }: { height: number }) {
  return (
    <div
      className="flex items-center justify-center bg-gray-50 dark:bg-gray-800/50 rounded-lg animate-pulse"
      style={{ height }}
    >
      <div className="w-32 h-32 rounded-full border-8 border-gray-200 dark:border-gray-700" />
    </div>
  )
})

const EmptyState = memo(function EmptyState({ height }: { height: number }) {
  return (
    <div
      className="flex flex-col items-center justify-center text-gray-400 dark:text-gray-500"
      style={{ height }}
    >
      <PieChartIcon className="h-12 w-12 mb-2" />
      <p className="text-sm">Veri bulunamadı</p>
    </div>
  )
})

const JiraStatusDonutChart = memo(function JiraStatusDonutChart({
  data,
  title = 'Durum Dağılımı',
  height = 240,
  loading = false,
  className = '',
  showLegend = true,
}: JiraStatusDonutChartProps) {
  const total = data.reduce((sum, item) => sum + item.value, 0)

  return (
    <Card variant="compact" padding="sm" className={className}>
      <CardHeader compact>
        <CardTitle compact className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
              <PieChartIcon className="h-4 w-4 text-purple-600 dark:text-purple-400" />
            </div>
            <span>{title}</span>
          </div>
          <span className="text-sm font-normal text-gray-500 dark:text-gray-400">
            {total} görev
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent compact>
        {loading ? (
          <LoadingState height={height} />
        ) : data.length === 0 ? (
          <EmptyState height={height} />
        ) : (
          <Suspense fallback={<LoadingState height={height} />}>
            <ChartContent data={data} height={height} />
            {showLegend && <Legend data={data} />}
          </Suspense>
        )}
      </CardContent>
    </Card>
  )
})

export default JiraStatusDonutChart

